import { useMemo, useState } from "react"
import { BulkAction, Data2, Labels, useDataTypesHooks, DataTypeList3, ListLoaderWrapper, DirectArchiveModal } from "../../../../../features/data-types"
import { Action, FilterField2, FieldType, Column, FieldValue, PanelWrapper, UIPermission, FilterField, option, ModalId, useUIStateActions, useUIActions, O, FiltersOptions } from "../../../../../features/ui"
import { DataType, TableName } from "../../../../../tables"
import { usePaymentTypeLoaders } from "../../../../administration"
import { PaymentStatus, usePriceOutlineHooks } from "../../../../operations"
import { useSystemStateHooks } from "../../../../system"
import { saleHelpers, useSaleHooks } from "../../../hooks/saleHooks"
import { SaleOrder } from "../../../state/types"
import { SellerField } from "../../seller-field/sellerField"
import { CustomerField } from "../../customer/customer-field/customerField"
import { useManagerHooks } from "../../../../manager"
import { useSaleLoaders } from "../../../loaders/saleLoaders"
import { useLocation, useNavigate } from "react-router-dom"
import { RoutePath } from "../../../../../features/routes"
import { DownloadSaleFilesModal } from "../../sale-operation/download-sale-files-modal/downloadSaleFilesModal"
import { saleOperationHelpers, useSaleOperationHooks } from "../../../hooks/saleOperationHooks"
import { toList } from "../../../../../utils/listUtils"
import { startOfPreviousMonth } from "../../../../../utils/typeUtils"
import { Params } from "../../../../../state/reducers/types"
import { useFind } from "../../../../../state/reducers/hooks"
import { FiscalDocumentStatus } from "../../../../fiscal-documents"
import { DeliveryStatus } from "../../../../deliveries"
import { BookmarkButton } from "../../../../../features/bookmarks"

export const SaleList = () => {
    const [currentSale, setCurrentSale] = useState<SaleOrder>()
    const [totalAmount, setTotalAmount] = useState<O<number>>()

    const { isInvoiceable } = saleHelpers
    const { isArchivable } = saleOperationHelpers
    const { userHasPermission } = useSystemStateHooks()
    const { loadSalesBatch, loadBatchData } = useSaleHooks()
    const { isEditableSync, isPayable, isDeliveryOrderEmitable } = useSaleOperationHooks()
    const { currentBranchId } = useManagerHooks()
    const { getPriceOutline } = usePriceOutlineHooks()
    const { formatPrice } = useDataTypesHooks()
    const navigate = useNavigate()
    const { pathname } = useLocation()

    const saleLoaders = useSaleLoaders()
    const paymentTypeLoaders = usePaymentTypeLoaders()
    const find = useFind()

    const { setBulkDataTypeIds } = useUIStateActions()
    const { openModal } = useUIActions()

    const readOnly = !userHasPermission(UIPermission.SAVE_SALES)
    const hasDeliveryPermission = userHasPermission(UIPermission.SAVE_SALE_DELIVERIES)
    const hasInvoicePermission = userHasPermission(UIPermission.INVOICE_SALES)

    const paymentTypeOptions = useMemo(() => paymentTypeLoaders().findAllPaymentTypes(true)
        .filter(paymentType => !paymentType.archived)
        .map(paymentType => option(paymentType.id, paymentType.name))
    , [])

    const filterFields: FilterField2[] = [
        {
            ...CustomerField({ showAddress: true, active: true }),
            space: 4
        } as FilterField,
        {
            ...SellerField({
                active: true
            }),
            space: 4
        } as FilterField,
        {
            name: 'dateFrom',
            type: FieldType.DATE,
            label: 'Fecha: Desde',
            space: 2
        },
        {
            name: 'dateTo',
            type: FieldType.DATE,
            label: 'Fecha: Hasta',
            space: 2
        },
        {
            name: 'paymentTypeId',
            type: FieldType.SELECT,
            options: paymentTypeOptions,
            label: 'Medio de Pago',
            space: 4
        },
        {
            name: 'latePayment',
            type: FieldType.SELECT,
            label: 'Modalidad de Pago',
            options: [
                { value: 'true', label: 'Contra Entrega' },
                { value: 'false', label: 'Anticipado' }
            ],
            space: 2
        },
        {
            name: 'paymentStatus',
            type: FieldType.SELECT,
            options: Object.values(PaymentStatus).map(status => option(status, Labels.paymentStatusLabel(status))),
            label: 'Estado de Pago',
            space: 2
        },
        {
            name: 'deliveryStatus',
            type: FieldType.SELECT,
            options: Object.values(DeliveryStatus).map(status => option(status, Labels.deliveryStatusLabel(status))),
            label: 'Estado de Entrega',
            space: 2
        },
        {
            name: 'saleStatus',
            type: FieldType.SELECT,
            label: 'Estado de Venta',
            defaultValue: 'active',
            options: [
                { value: 'active', label: 'Activa' },
                { value: 'cancelled', label: 'Cancelada' }
            ],
            space: 2
        }
    ]

    const filtersOptions: FiltersOptions = {
        filtersKey: 'sales',
        search: {
            placeholder: 'Buscar por código'
        },
        filterFields,
        // Por defecto solo el mes en curso y el anterior, para no cargar todo el historial
        defaultFiltersData: { dateFrom: startOfPreviousMonth() },
        filtersTextAffix: currentBranchId
    }

    const columns: Column[] = [
        {
            name: 'code',
            type: FieldType.NUMBER,
            label: '#',
            render: (value?: FieldValue) => `#${value}`
        },
        {
            name: 'creationDate',
            type: FieldType.DATE,
            label: 'Fecha de Creación'
        },
        {
            name: 'deliveryDate',
            type: FieldType.DATE,
            label: 'Fecha de Entrega'
        },
        {
            name: 'sellerId',
            type: FieldType.TABLE,
            table: TableName.EMPLOYEES,
            label: 'Vendedor',
            sortValue: (value?: FieldValue) => (find(TableName.EMPLOYEES, value as string) as { name?: string })?.name ?? ''
        },
        {
            name: 'customerId',
            type: FieldType.TABLE,
            table: TableName.CUSTOMERS,
            label: 'Cliente',
            sortValue: (value?: FieldValue) => (find(TableName.CUSTOMERS, value as string) as { name?: string })?.name ?? ''
        },
        {
            name: 'deliveryStatus',
            type: FieldType.TEXT,
            label: 'Estado de Entrega',
            sortValue: (value?: FieldValue) => Labels.deliveryStatusLabel(value as DeliveryStatus),
            render: (value?: FieldValue) => Labels.deliveryStatusLabel(value! as DeliveryStatus)
        },
        {
            name: 'paymentStatus',
            type: FieldType.TEXT,
            label: 'Estado de Pago',
            sortValue: (value?: FieldValue) => Labels.paymentStatusLabel(value as PaymentStatus),
            render: (value?: FieldValue) => Labels.paymentStatusLabel(value! as PaymentStatus)
        },
        {
            name: 'invoiceStatus',
            type: FieldType.TEXT,
            label: 'Facturación',
            sortValue: (value?: FieldValue) => Labels.fiscalDocumentStatusLabel(value as FiscalDocumentStatus),
            render: (value?: FieldValue) => Labels.fiscalDocumentStatusLabel(value! as FiscalDocumentStatus)
        }
    ]

    const onBulkConfirmPayments = (ids: string[]) => {
        setBulkDataTypeIds(ids)
        navigate(RoutePath.CONFIRM_PAYMENT)
    }

    const isSuitableForPayment = async (id: string) => {
        const { loadSale } = saleLoaders()
        const sale = await loadSale(id)
        return isPayable(sale!)
    }

    const bulkActions: BulkAction[] = useMemo(() => [{
        id: 'confirm-payment',
        label: 'Confirmar Pagos',
        do: onBulkConfirmPayments,
        isSuitable: isSuitableForPayment
    }], [])

    const toRow = (saleDT: DataType): Data2 => {
        const { items, paymentItems, deliveryData, ...data } = saleDT as SaleOrder
        return {
            ...data,
            deliveryDate: deliveryData?.deliveryDate,
            check: saleDT.id
        }
    }

    const onDownloadFiles = (sale: SaleOrder) => () =>{
        setCurrentSale(sale)
        openModal(ModalId.DOWNLOAD_FILES)
    }

    const onEmitDelivery = (id?: string) => navigate(`${id!}/${RoutePath.EMIT_DELIVERY}`)
    
    const onInvoice = (id?: string) => navigate(`${id!}/${RoutePath.INVOICE}`)
    
    const onArchive = (sale: SaleOrder) => () => {
        setCurrentSale(sale)
        openModal(ModalId.DIRECT_ARCHIVE)
    }

    const actions = async (rowIdValue: string) => {
        const { loadSale } = saleLoaders()
        const sale = await loadSale(rowIdValue)

        const saleHref = `${pathname}/${rowIdValue}`
        const actionList: Action[] = [
            { icon: 'eye-fill', kind: 'tertiary', tooltip: 'Ver Detalle', href: saleHref },
            { icon: 'download', kind: 'tertiary', tooltipLines: ['Descargar', 'Documentos'], onClick: onDownloadFiles(sale!) }
        ]

        if (!readOnly) {
            if (isEditableSync(sale!)) {
                actionList.push({ icon: 'pencil-fill', tooltip: 'Editar', href: `${saleHref}/${RoutePath.EDIT}` })
            }
            if (hasDeliveryPermission && isDeliveryOrderEmitable(sale!)) {
                actionList.push({ icon: 'calendar-range', tooltip: 'Emitir Entrega', onClick: onEmitDelivery })
            }
            if (isPayable(sale!)) {
                actionList.push({ icon: 'check-lg', tooltip: 'Confirmar Pago', href: `${saleHref}/${RoutePath.CONFIRM_PAYMENT}` })
            }
        }
        if (hasInvoicePermission && isInvoiceable(sale!)) {
            actionList.push({ icon: 'file-earmark-check', tooltip: 'Facturar', kind: 'quinary', onClick: onInvoice })
        }
        if (!readOnly && isArchivable(sale!)) {
            actionList.push({ icon: 'archive-fill', kind: 'secondary', tooltip: 'Archivar Venta', onClick: onArchive(sale!) })
        }

        return actionList
    }

    const infoRows = useMemo(() => totalAmount ?
        toList({ label: 'Total Ventas', value: formatPrice(totalAmount) }) :
        []
    , [totalAmount, formatPrice])

    const showTotalAmount = (filtersData?: Data2) => {
        const { customerId, paymentTypeId, sellerId, dateFrom, dateTo } = filtersData || {}
        const toDays = (ms: number) => Math.round(ms / (1000 * 60 * 60 * 24))
        const datesCondition = (days = 365) => dateFrom && dateTo && (toDays(new Date(dateTo as string).getTime() - new Date(dateFrom as string).getTime()) <= days)
        return ((customerId || paymentTypeId || sellerId) && datesCondition()) || datesCondition(62)
    }

    const loadBatch = (
        filtersData?: Data2,
        batchSize?: number,
        startKey?: Params
    ) => {
        return loadSalesBatch(filtersData, showTotalAmount(filtersData) ? undefined : batchSize, startKey)
    }
    
    const onLoadBatch = (filtersData: Data2, salesDT: DataType[]) => {
        setTotalAmount(showTotalAmount(filtersData) ?
            salesDT.reduce((acc, saleDT) => {
                const priceOutline = getPriceOutline(TableName.SALE_ORDERS, saleDT as SaleOrder)
                return acc + priceOutline.total
            }, 0) :
            undefined
        )
    }

    return (
        <ListLoaderWrapper table={TableName.SALE_ORDERS}>
            <>
                <PanelWrapper
                    title="Órdenes de Venta"
                    wide
                    titleRight={<BookmarkButton routePath={RoutePath.SALES} />}
                >
                    <DataTypeList3
                        columns={columns}
                        filtersOptions={filtersOptions}
                        canSort={filtersData => !!filtersData.dateFrom || !!filtersData.customerId}
                        bulkActions={bulkActions}
                        infoRows={infoRows}
                        readOnly={readOnly}
                        toRow={toRow}
                        actions={actions}
                        loadBatch={loadBatch}
                        loadBatchData={loadBatchData}
                        onLoadBatch={onLoadBatch}
                    />
                </PanelWrapper>
                <DownloadSaleFilesModal
                    table={TableName.SALE_ORDERS}
                    saleOperation={currentSale}
                />
                <DirectArchiveModal
                    tableName={TableName.SALE_ORDERS}
                    dataType={currentSale}
                />
            </>
        </ListLoaderWrapper>
    )
}
